/**
 * Blocks that can only be a plain paragraph:
 * - Indented text (decision 35). CommonMark makes a line indented four columns past its container an
 *   indented code block; with code blocks off, Lezer would instead let it open a quote, a list, a heading
 *   or a scene break. Here it is an ordinary `Paragraph`.
 * - Anything nested deeper than {@link MAX_BLOCK_DEPTH} blocks. Markup nested deeper than a novel needs
 *   reads as text, so no input can build a tree deep enough to overflow a recursive consumer. A list's next
 *   item still opens at the limit: a list holds nothing but items.
 *
 * Such a block's first line opens no construct, and no leaf rule (link reference definition, setext
 * heading, centered block) takes the block over. Its continuation lines follow the normal paragraph rules,
 * with one addition: every thematic break ends it. Lezer lets a `---` line through when a setext heading could
 * claim it, and in a plain paragraph none can, so `---` would otherwise join two paragraphs.
 */
import type { BlockContext, LeafBlock, Line, MarkdownConfig } from '@lezer/markdown';
import { isSceneBreak } from './scene-break.ts';

/**
 * A block opens only while fewer than this many are open, counting the document: 31 levels of quotes fit.
 * A list opens together with its first item, so containers reach one more at most, and the leaf block
 * inside them one more again. Deeper markup is paragraph text.
 */
export const MAX_BLOCK_DEPTH = 32;

/** Where each parse's current plain paragraph starts, so the leaf rule can recognise it. */
const plainStarts = new WeakMap<BlockContext, number>();

/** Lists, whose children can only be list items. */
const LISTS = new Set(['BulletList', 'OrderedList']);

/**
 * Whether a block starting on this line must be a plain paragraph. At the depth limit, a line inside a list
 * (its previous item just ended) is that list's next item, not a paragraph. An indented line never meets a
 * list here: indentation that deep closes the list first.
 */
function isPlain(cx: BlockContext, line: Line): boolean {
  return line.indent >= line.baseIndent + 4 || (cx.depth >= MAX_BLOCK_DEPTH && !LISTS.has(cx.parentType().name));
}

/**
 * The extension. `PlainParagraph` runs before every other block parser: on such a line it records the
 * block's start and hides the line's first character (`line.next`, the only thing Lezer's block constructs
 * look at to open), so the line falls through to a paragraph. `PlainParagraphLeaf` runs after every other
 * leaf rule and drops them all for that block, and ends it on a thematic break.
 */
export const plainParagraph: MarkdownConfig = {
  parseBlock: [
    {
      name: 'PlainParagraph',
      before: 'Blockquote',
      parse(cx: BlockContext, line: Line): boolean {
        if (isPlain(cx, line)) {
          plainStarts.set(cx, cx.lineStart + line.pos);
          line.next = -1;
        }
        return false;
      },
    },
    {
      name: 'PlainParagraphLeaf',
      after: 'SetextHeading',
      leaf(cx: BlockContext, leaf: LeafBlock): null {
        if (plainStarts.get(cx) === leaf.start) leaf.parsers = [];
        return null;
      },
      endLeaf(cx: BlockContext, line: Line, leaf: LeafBlock): boolean {
        return plainStarts.get(cx) === leaf.start && isSceneBreak(line);
      },
    },
  ],
};
